/**
 * Dialog Context
 *
 * This module provides a centralized system for managing all modals and dialogs in the application.
 * It implements the React Context API pattern to provide dialog functionality throughout the component tree.
 *
 * Features:
 * - Unified API for showing different types of dialogs
 * - State management for dialog visibility
 * - Support for various dialog types (alerts, confirmations, forms, etc.)
 * - Consistent dialog styling and behavior
 *
 * Usage:
 * ```jsx
 * // In a component:
 * import { useContext } from 'react';
 * import { DialogContext } from 'path/to/dialog-context';
 *
 * function MyComponent() {
 *   const { showAlertDialogBox } = useContext(DialogContext);
 *
 *   const handleAction = () => {
 *     showAlertDialogBox({
 *       title: 'Success',
 *       message: 'Operation completed',
 *       dialogType: 'success'
 *     });
 *   };
 *
 *   return <button onClick={handleAction}>Show Dialog</button>;
 * }
 * ```
 */

import { createContext, useRef, useState } from "react";
import AlertDialog from "../components/funnel-form/alert-dialog";
import SyncLeadDialog from "../components/leads/sync-lead-dialog";
import AddDomainModal from "../components/funnels-table/add-domain-modal";
import CreateNewModal from "../components/create-new-modal/create-new-modal";
import MoveCustomerDialog from "../components/customers/move-customer-dialog";
import WebsiteReplaceDialog from "../components/customers/website-replace-dialog";
import ImageDetailsDialog from "../components/ads-evaluation/image-details-dialog";
import SaveAsTemplateDialog from "../components/funnel-form/save-as-template-dialog";
import WebsiteAIPromptDialog from "../components/customers/website-ai-prompt-dialog";
import WebsiteDuplicateDialog from "../components/customers/website-duplicate-dialog";
import ContactCloudSetupDialog from "../components/funnel-integration/contact-cloud-setup-dialog";
import EditWebsiteSubPageDialog from "../components/customers/website/local-seo/edit-sub-page-dialog";
import ConfirmationDialog from "../components/funnel-form/funnel-custom-accordion/confirmation-dialog";
import DisableLeadServiceConfirmationDialog from "../components/funnel-integration/integration-view/disable-leadservice-confirmation-dialog";

/**
 * Dialog Context object - provides access to dialog functions throughout the app
 * @type {React.Context<{}>}
 */
export const DialogContext = createContext({});

/**
 * Initial state for dialog data
 * Used to reset dialog state when dialogs are closed
 * @type {Object}
 */
const initialState = {
  type: "", // Dialog type identifier
  title: "", // Dialog title
  message: "", // Dialog message content
  dialogType: "", // Style/category of dialog (warning, success, etc.)
  modaltype: "" // Type of modal for specific cases
};

/**
 * Constants defining all available dialog types
 * Used to map dialog type to rendering function
 * @type {Object}
 */
const DIALOG_TYPES = {
  ALERT: "ALERT", // Simple alert dialog
  SYNC_LEAD: "SYNC_LEAD", // Lead data synchronization dialog
  ADD_DOMAIN: "ADD_DOMAIN", // Add domain to funnel dialog
  CREATE_NEW: "CREATE_NEW", // Create new entity dialog
  CONFIRMATION: "CONFIRMATION", // Generic confirmation dialog
  IMAGE_DETAILS: "IMAGE_DETAILS", // Image details/preview dialog
  WEBSITE_AI_PROMPT: "WEBSITE_AI_PROMPT", //Website ai prompt dialog
  SAVE_AS_TEMPLATE: "SAVE_AS_TEMPLATE", // Save funnel as template dialog
  CONTACT_CLOUD_SETUP: "CONTACT_CLOUD_SETUP", // Contact cloud integration setup
  REPLACE_WEB_TEMPLATE: "REPLACE_WEB_TEMPLATE", // Website template replacement dialog
  MOVE_CUSTOMER: "MOVE_CUSTOMER", // Website move customer dialog
  EDIT_WEBSITE_LOCAL_SEO: "EDIT_WEBSITE_LOCAL_SEO", // Edit Website local seo sub-page
  DUPLICATE_WEB_TEMPLATE: "DUPLICATE_WEB_TEMPLATE", // Website template duplication dialog
  DISABLE_LEAD_SERVICE_CONFIRMATION: "DISABLE_LEAD_SERVICE_CONFIRMATION" // Lead service disabling confirmation
};

/**
 * Dialog Provider Component
 *
 * Provides dialog functionality to all children components through React Context.
 * Manages dialog state, visibility, and renders the appropriate dialog component.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to dialog context
 * @returns {JSX.Element} Context provider with dialog functionality
 */
export function DialogProvider({ children }) {
  // State for dialog content data
  const [dialogData, setDialogData] = useState(initialState);
  // State for dialog visibility
  const [showModal, setShowModal] = useState(false);
  // Ref to store additional state that dialogs may need
  const dependentStatesRef = useRef({});

  // Destructuring dialog types for easier reference
  const {
    ALERT,
    SYNC_LEAD,
    ADD_DOMAIN,
    CREATE_NEW,
    CONFIRMATION,
    IMAGE_DETAILS,
    MOVE_CUSTOMER,
    SAVE_AS_TEMPLATE,
    WEBSITE_AI_PROMPT,
    CONTACT_CLOUD_SETUP,
    REPLACE_WEB_TEMPLATE,
    DUPLICATE_WEB_TEMPLATE,
    EDIT_WEBSITE_LOCAL_SEO,
    DISABLE_LEAD_SERVICE_CONFIRMATION
  } = DIALOG_TYPES;

  const setDependentStates = (value) => {
    dependentStatesRef.current = value;
  };

  const toggleModal = (value) => {
    if (value || !showModal) {
      setShowModal(true);
    } else {
      setShowModal(false);
      setDialogData(initialState);
    }
  };

  const showAlertDialogBox = ({
    message,
    dialogType,
    responseAction,
    title,
    acceptLabel,
    showFooter,
    lsIndividualNote
  }) => {
    setDialogData({
      title,
      message,
      type: ALERT,
      dialogType,
      responseAction,
      acceptLabel,
      showFooter,
      lsIndividualNote
    });
    toggleModal();
  };

  const showConfirmationDialogBox = ({
    dialogMessage,
    dialogType,
    responseAction,
    title,
    acceptLabel,
    declineLabel,
    disableAcceptBtn,
    isSync
  }) => {
    setDialogData({
      title,
      type: CONFIRMATION,
      dialogType,
      responseAction,
      acceptLabel,
      declineLabel,
      disableAcceptBtn,
      dialogMessage
    });
    toggleModal(isSync);
  };

  const showSaveAsTemplateDialogBox = ({ formValues, resourceId }) => {
    setDialogData({
      resourceId,
      formValues,
      type: SAVE_AS_TEMPLATE
    });
    toggleModal();
  };

  const showDuplicateWebTemplateDialogBox = ({
    templateType,
    formValues,
    customerId,
    tabRoute
  }) => {
    setDialogData({
      templateType,
      customerId,
      tabRoute,
      formValues,
      type: DUPLICATE_WEB_TEMPLATE
    });
    toggleModal();
  };

  const showAIPromptDialogBox = ({
    generateContent,
    handleAutoSave,
    customerId,
    customerContactData
  }) => {
    setDialogData({
      customerId,
      customerContactData,
      generateContent,
      handleAutoSave,
      type: WEBSITE_AI_PROMPT
    });
    toggleModal();
  };

  const showEditWebsiteSubPageDialogBox = ({ data, onAfterEditSuccess }) => {
    setDialogData({ data, onAfterEditSuccess, type: EDIT_WEBSITE_LOCAL_SEO });
    toggleModal();
  };

  const showReplaceWebTemplateDialogBox = ({
    templateId,
    template,
    customerId
  }) => {
    setDialogData({
      templateId,
      customerId,
      template,
      type: REPLACE_WEB_TEMPLATE
    });
    toggleModal();
  };

  const showMoveCustomerDialogBox = ({ customerId, fetchCustomersData }) => {
    setDialogData({
      customerId,
      fetchCustomersData,
      type: MOVE_CUSTOMER
    });
    toggleModal();
  };

  const showSyncLeadDialogBox = ({ leadInfo, fetchLeads }) => {
    setDialogData({
      leadInfo,
      fetchLeads,
      type: SYNC_LEAD
    });
    toggleModal();
  };

  const showAddDomainDialogBox = ({ getFunnels, resourceId }) => {
    setDialogData({
      getFunnels,
      resourceId,
      type: ADD_DOMAIN
    });
    toggleModal();
  };

  const showCreateNewDialogBox = ({ url, customer, modaltype }) => {
    setDialogData({
      url,
      customer,
      type: CREATE_NEW,
      modaltype
    });
    toggleModal();
  };

  const showContactCloudDialogBox = ({
    funnel,
    funnelId,
    setIntegrations,
    formData
  }) => {
    setDialogData({
      type: CONTACT_CLOUD_SETUP,
      funnel,
      funnelId,
      setIntegrations,
      formData
    });
    toggleModal();
  };

  const showImageDetailsDialogBox = ({ data }) => {
    setDialogData({
      type: IMAGE_DETAILS,
      data
    });
    toggleModal();
  };

  const showDisableLeadServiceConfirmationDialogBox = ({
    title,
    dialogType,
    dialogMessage,
    responseAction
  }) => {
    setDialogData({
      title,
      type: DISABLE_LEAD_SERVICE_CONFIRMATION,
      dialogType,
      responseAction,
      dialogMessage
    });
    toggleModal();
  };

  const dialogTypes = {
    [ALERT]: () => (
      <AlertDialog
        description={dialogData.message}
        show={showModal}
        setShow={toggleModal}
        dialogType={dialogData.dialogType}
        title={dialogData.title}
        showFooter={dialogData.showFooter}
        acceptLabel={dialogData.acceptLabel}
        lsIndividualNote={dialogData.lsIndividualNote}
      />
    ),
    [CONFIRMATION]: () => (
      <ConfirmationDialog
        title={dialogData.title}
        dialogType={dialogData.dialogType}
        show={showModal}
        toggleDialog={toggleModal}
        dependentStates={dependentStatesRef.current}
        dialogResponseAction={dialogData.responseAction}
        renderDialogMessage={dialogData.dialogMessage}
        acceptLabel={dialogData.acceptLabel}
        declineLabel={dialogData.declineLabel}
        disableAcceptBtn={dialogData.disableAcceptBtn}
      />
    ),
    [SAVE_AS_TEMPLATE]: () => (
      <SaveAsTemplateDialog
        show={showModal}
        formValues={dialogData.formValues}
        toggleDialog={toggleModal}
        resourceId={dialogData.resourceId}
      />
    ),
    [DUPLICATE_WEB_TEMPLATE]: () => (
      <WebsiteDuplicateDialog
        show={showModal}
        // mainFormValues={dialogData.formValues}
        toggleDialog={toggleModal}
        customerId={dialogData.customerId}
        tabRoute={dialogData.tabRoute}
        // templateType={dialogData.templateType}
      />
    ),
    [WEBSITE_AI_PROMPT]: () => (
      <WebsiteAIPromptDialog
        show={showModal}
        toggleDialog={toggleModal}
        customerId={dialogData.customerId}
        generateContent={dialogData.generateContent}
        customerContactData={dialogData.customerContactData}
      />
    ),
    [EDIT_WEBSITE_LOCAL_SEO]: () => (
      <EditWebsiteSubPageDialog
        show={showModal}
        toggleDialog={toggleModal}
        data={dialogData.data}
        onAfterEditSuccess={dialogData.onAfterEditSuccess}
      />
    ),
    [REPLACE_WEB_TEMPLATE]: () => (
      <WebsiteReplaceDialog
        show={showModal}
        template={dialogData.template}
        toggleDialog={toggleModal}
        customerId={dialogData.customerId}
        templateId={dialogData.templateId}
      />
    ),
    [MOVE_CUSTOMER]: () => (
      <MoveCustomerDialog
        show={showModal}
        toggleDialog={toggleModal}
        customerId={dialogData.customerId}
        fetchCustomersData={dialogData.fetchCustomersData}
      />
    ),
    [SYNC_LEAD]: () => (
      <SyncLeadDialog
        leadInfo={dialogData.leadInfo}
        show={showModal}
        fetchLeads={dialogData.fetchLeads}
        toggleDialog={toggleModal}
      />
    ),
    [ADD_DOMAIN]: () => (
      <AddDomainModal
        show={showModal}
        getFunnels={dialogData.getFunnels}
        resourceId={dialogData.resourceId}
        toggleDialog={toggleModal}
      />
    ),
    [CREATE_NEW]: () => (
      <CreateNewModal
        show={showModal}
        queryparams={dialogData.queryparams}
        url={dialogData.url}
        customer={dialogData.customer}
        modaltype={dialogData.modaltype}
        onHide={() => toggleModal(false)}
      />
    ),
    [CONTACT_CLOUD_SETUP]: () => (
      <ContactCloudSetupDialog
        show={showModal}
        toggleDialog={toggleModal}
        funnel={dialogData.funnel}
        funnelId={dialogData.funnelId}
        setIntegrations={dialogData.setIntegrations}
        formData={dialogData.formData}
      />
    ),
    [IMAGE_DETAILS]: () => (
      <ImageDetailsDialog
        show={showModal}
        toggleModal={toggleModal}
        data={dialogData.data}
      />
    ),
    [DISABLE_LEAD_SERVICE_CONFIRMATION]: () => (
      <DisableLeadServiceConfirmationDialog
        show={showModal}
        title={dialogData.title}
        toggleDialog={toggleModal}
        dialogType={dialogData.dialogType}
        renderDialogMessage={dialogData.dialogMessage}
        dialogResponseAction={dialogData.responseAction}
      />
    )
  };

  return (
    <DialogContext.Provider
      value={{
        setDependentStates,
        showAlertDialogBox,
        showSyncLeadDialogBox,
        showAddDomainDialogBox,
        showCreateNewDialogBox,
        showConfirmationDialogBox,
        showContactCloudDialogBox,
        showImageDetailsDialogBox,
        showSaveAsTemplateDialogBox,
        showReplaceWebTemplateDialogBox,
        showMoveCustomerDialogBox,
        showDuplicateWebTemplateDialogBox,
        showAIPromptDialogBox,
        showEditWebsiteSubPageDialogBox,
        showDisableLeadServiceConfirmationDialogBox,
        dependentStates: dependentStatesRef.current
      }}
    >
      {children}
      {dialogTypes[dialogData.type]?.()}
    </DialogContext.Provider>
  );
}
